import React, { useState, useEffect } from "react";
import { Grid, Tab, Tabs, Typography } from "@mui/material";
import { setsConfigBetaTabs } from "../../utils/constants";
import {
  MAutocomplete,
  MTextField,
  MDatePicker,
  TabPanel,
  ResponsiveFormContainer,
} from "@app/components";
import { useEquipmentMide, useUser } from "@services/servicesGenericsTables";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { useFormContext } from "react-hook-form";
import ChartAlfaAbsorptionForm from "./chartAlfaAbsorption";

interface AnalysisFormProps {
  mode: string;
}

const AnalysisBetaForm = ({ mode }: AnalysisFormProps) => {
  const { userList } = useUser();
  const [tabValue, setTabValue] = useState(0);
  const isView = mode === MODES_OPTIONS.VIEW;
  const { equipmentMideListFiltered, loading } = useEquipmentMide();
  const { watch, setValue, getValues } = useFormContext();

  useEffect(() => {
    if (getValues("equipment")) return;
    const equipment = equipmentMideListFiltered.find(
      (equipment) => equipment.codigo === "3002" //CODIGO DE EQUIPO DE CENTELLEO
    );
    setValue("equipment", equipment || undefined);
  }, [equipmentMideListFiltered]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <>
      <Tabs
        value={tabValue}
        onChange={handleChange}
        aria-label="Fields uncertainty beta tabs"
      >
        {setsConfigBetaTabs.map((set, index) => (
          <Tab key={index} label={set.label} />
        ))}
      </Tabs>
      <TabPanel value={tabValue} index={0} padding={1}>
        <ResponsiveFormContainer columns={4}>
          <MAutocomplete
            name="analyst"
            options={userList}
            label="Seleccione un analista"
            getOptionLabel={(option) => option.username || ""}
            validation={{ required: "El analista es requerido" }}
            disabled={isView}
          />
          <MDatePicker
            name="date"
            pickerType="date"
            label="Fecha calibración"
            validation={{ required: "La fecha es requerida" }}
            disabled={isView}
          />

          <MAutocomplete
            key={JSON.stringify(watch("equipment"))}
            name="equipment"
            options={equipmentMideListFiltered}
            label="Contador Proporcional Alfa-Beta"
            getOptionLabel={(option) =>
              `${option.codigo} - ${option.descripcion}` || ""
            }
            loading={loading}
          />
        </ResponsiveFormContainer>

        <Grid item xs={12} my={2}>
          <Typography variant="tagText">{"Datos patrón primario"}</Typography>
        </Grid>
        <ResponsiveFormContainer columns={2}>
          <MTextField
            name="ta0"
            label="Actividad del patrón primario (Bq/g)"
            placeholder="Actividad del patrón primario (Bq/g)"
            type="number"
            validation={{ required: "La actividad es requerida" }}
            fullWidth
            disabled={isView}
          />
          <MTextField
            name="tua0"
            label="Incertidumbre patrón primario para k=2 (Bq/g)"
            helperText="(*) Aplica los datos del certificado de la fuente patrón"
            placeholder="Incertidumbre patrón primario para k=2 (Bq/g)"
            type="number"
            validation={{ required: "La incertidumbre es requerida" }}
            fullWidth
            disabled={isView}
          />
        </ResponsiveFormContainer>

        <Grid item xs={12} my={2}>
          <Typography variant="tagText">
            {"Datos preparación patrón secundario"}
          </Typography>
        </Grid>
        <ResponsiveFormContainer columns={2}>
          <MTextField
            name="tmpp"
            label="Masa patrón primario (mpp) (mg)"
            placeholder="Masa patrón primario (mpp) (mg)"
            type="number"
            validation={{ required: "La masa es requerida" }}
            fullWidth
            disabled={isView}
          />
          <MTextField
            name="tmps"
            label="Volumen patrón secundario (mps) (ml)"
            placeholder="Volumen patrón secundario (mps) (ml)"
            type="number"
            validation={{ required: "La masa es requerida" }}
            fullWidth
            disabled={isView}
          />
        </ResponsiveFormContainer>

        <Grid item xs={12} my={2}>
          <Typography variant="tagText">
            {"Datos preparación fuente de calibración"}
          </Typography>
        </Grid>
        <ResponsiveFormContainer columns={2}>
          <MTextField
            name="vol_of_sec_in_calibration_source_at_0"
            label="Volumen del patrón secundario en la fuente de calibración a espesor cero (ml)"
            placeholder="Volumen del patrón secundario en la fuente de calibración a espesor cero (ml)"
            type="number"
            validation={{ required: "El volumen es requerido" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="sec_pattern_activity_at_0_cal_s_at_count_d"
            label="Actividad del patrón secundario en la fuente de calibración de espesor cero a fecha de contaje (dpm)"
            placeholder="Actividad del patrón secundario en la fuente de calibración de espesor cero a fecha de contaje (dpm)"
            type="number"
            validation={{ required: "La actividad es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="cal_source_efficiency_at_0"
            label="Eficiencia de la fuente de calibración a espesor cero"
            placeholder="Eficiencia de la fuente de calibración a espesor cero"
            type="number"
            validation={{ required: "La eficiencia es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="cpm_of_cal_source_with_quantity_at_0"
            label="cpm de la fuente de calibración con cantidad a espesor cero"
            placeholder="cpm de la fuente de calibración con cantidad a espesor cero"
            type="number"
            validation={{ required: "La cpm es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="cal_source_counting_time_to_0_in_total_detectors"
            label="Tiempo de contaje de la fuente de calibración a espesor cero en total de detectores (min)"
            placeholder="Tiempo de contaje de la fuente de calibración a espesor cero en total de detectores (min)"
            type="number"
            validation={{ required: "El tiempo de contaje es requerido" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="uncertainty_calibration_source_counting_time_without_extinguishing_agent"
            label="Incertidumbre relativa al cuadrado del factor de auto absorción (U2relFa)"
            placeholder="Incertidumbre relativa al cuadrado del factor de auto absorción (U2relFa)"
            type="number"
            fullWidth
            disabled
          />
        </ResponsiveFormContainer>

        <ResponsiveFormContainer columns={2} mt={2}>
          <MTextField
            name="internal_comments"
            label="Comentarios Internos"
            placeholder="Comentarios Internos"
            type="textArea"
            rows={4}
            multiline
            fullWidth
            disabled={isView}
            step={0.01}
          />
        </ResponsiveFormContainer>
      </TabPanel>
      <TabPanel value={tabValue} index={1} padding={1}>
        <ChartAlfaAbsorptionForm mode={mode} />
      </TabPanel>

      <TabPanel value={tabValue} index={2} padding={1}>
        <Grid item xs={12} my={2}>
          <Typography variant="tagText">
            {"Datos de la determinacióin del spill-over en la medida Beta"}
          </Typography>
        </Grid>
        <ResponsiveFormContainer columns={4}>
          <MTextField
            name="cpm_of_the_alpha_pattern_in_the_beta_win"
            label="cpm del patrón Alfa en la ventana Beta (cpm)"
            placeholder="cpm del patrón Alfa en la ventana Beta (cpm)"
            type="number"
            validation={{ required: "La cpm es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="cpm_of_the_alpha_pattern_in_the_alfa_win"
            label="cpm del patrón Alfa en la ventana Alfa (cpm)"
            placeholder="cpm del patrón Alfa en la ventana Alfa (cpm)"
            type="number"
            validation={{ required: "La cpm es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="total_counting_time_alpha_pattern_in_total_detectors"
            label="Tiempo total de contaje del patrón Alfa en el total de detectores (min)"
            placeholder="Tiempo total de contaje del patrón Alfa en el total de detectores (min)"
            type="number"
            validation={{ required: "El tiempo de contaje es requerido" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
        </ResponsiveFormContainer>
      </TabPanel>
      <TabPanel value={tabValue} index={3} padding={1}>
        <Grid item xs={12} my={2}>
          <Typography variant="tagText">
            {"Datos de la determinación del spill-over en la medida Beta"}
          </Typography>
        </Grid>
        <ResponsiveFormContainer columns={4}>
          <MTextField
            name="mass_of_the_torta_of_fc_with_the_lowest_amount"
            label="Masa de la torta de la f. c. con menor cantidad de KCl (mg)"
            placeholder="Masa de la torta de la f. c. con menor cantidad de KCl (mg)"
            type="number"
            validation={{
              required: "La cpm es requerida",
              validate: (value) =>
                value !== "0" || "El valor debe ser distinto de cero",
            }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="efficiency_of_the_torta_of_fc_with_the_lowest_amount"
            label="Eficiencia de la torta de la f.c. con menor cantidad de KCl"
            placeholder="Eficiencia de la torta de la f.c. con menor cantidad de KCl"
            type="number"
            validation={{
              required: "La cpm es requerida",
              validate: (value) =>
                value !== "0" || "El valor debe ser distinto de cero",
            }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="cpm_of_the_torta_of_fc_with_the_lowest_amount"
            label="cpm de la torta de la f. c. con menor cantidad de KC"
            placeholder="cpm de la torta de la f. c. con menor cantidad de KC"
            type="number"
            validation={{
              required: "La cpm es requerida",
              validate: (value) =>
                value !== "0" || "El valor debe ser distinto de cero",
            }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="total_counting_time_of_the_torta_of_fc_with_the_lowest_amount"
            label="Tiempo total de contaje de la torta de la f.c. con menor cantidad de KCl en el total de detectores (min)"
            placeholder="Tiempo total de contaje de la torta de la f.c. con menor cantidad de KCl en el total de detectores (min)"
            type="number"
            validation={{
              required: "La cpm es requerida",
              validate: (value) =>
                value !== "0" || "El valor debe ser distinto de cero",
            }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="uncertainty_calibration_source_counting_time_without_extinguishing_agent_k_40"
            label="Incertidumbre relativa al cuadrado del factor de auto absorción de la calibración de K"
            placeholder="Incertidumbre relativa al cuadrado del factor de auto absorción de la calibración de K"
            type="number"
            validation={{
              required: "La cpm es requerida",
              validate: (value) =>
                value !== "0" || "El valor debe ser distinto de cero",
            }}
            fullWidth
            disabled
            step={0.01}
          />
        </ResponsiveFormContainer>
      </TabPanel>
      <TabPanel value={tabValue} index={4} padding={1}>
        <ChartAlfaAbsorptionForm
          mode={mode}
          nameTable="rowsCurvaK40"
          nameUncertainty="uncertainty_calibration_source_counting_time_without_extinguishing_agent_k_40"
        />
      </TabPanel>
    </>
  );
};

export default AnalysisBetaForm;
